import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { OTPService } from '@/core/services/otpService';
import Loading from '@/core/components/globals/loading';
import { toast } from 'react-toastify';
import Input from '@/core/components/dv-forms/input';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

interface WithdrawOTPModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const WithdrawOTPModal: React.FC<WithdrawOTPModalProps> = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [isLoading] = useState<boolean>(true);
  const [totpNotSetup, setTotpNotSetup] = useState<boolean>(false);

  // Check TOTP status when component mounts
  // const { isLoading: isStatusLoading } = useQuery({
  //   queryKey: ['totp-status'],
  //   queryFn: OTPService.getTOTPStatus,
  //   onSuccess: (data) => {
  //     setIsLoading(false);
  //     if (!data.status) {
  //       setTotpNotSetup(true);
  //     }
  //   },
  //   onError: () => {
  //     setIsLoading(false);
  //     setTotpNotSetup(true);
  //   }
  // });

  const { register, handleSubmit, formState: { errors, isValid } } = useForm({
    mode: 'onChange',
    defaultValues: {
      code: ''
    }
  });

  // Verify TOTP mutation
  const { mutate: verifyTOTP, isLoading: isVerifying } = useMutation({
    mutationFn: (code: string) => OTPService.verifyTOTP({ code }),
    onSuccess: (data) => {
      if (data.status === "success") {
        toast.success(t('message.otp_verified'));
        onSuccess();
      } else {
        toast.error(data.message || t('message.otp_verification_error'));
      }
    },
    onError: (error: any) => {
      // Check if the error is due to TOTP not being set up
      if (error?.response?.status === 404 ||
          (error?.response?.data?.message &&
           error?.response?.data?.message.includes("TOTP not found"))) {
        setTotpNotSetup(true);
      } else {
        toast.error(error?.response?.data?.message || t('message.otp_verification_error'));
      }
    }
  });

  // Handle form submission
  const onSubmit = (values: any) => {
    verifyTOTP(values.code);
  };

  /* SMS OTP code preserved as requested (not used)
  // Original SMS OTP implementation
  const [phoneNumberMissing, setPhoneNumberMissing] = useState<boolean>(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(60);
  const [canResend, setCanResend] = useState<boolean>(false);

  // Request OTP when component mounts
  useEffect(() => {
    requestOTP();
  }, []);

  // Timer for resending OTP
  useEffect(() => {
    if (timeRemaining > 0 && !canResend) {
      const timer = setTimeout(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0 && !canResend) {
      setCanResend(true);
    }
  }, [timeRemaining, canResend]);

  // Request OTP mutation
  const { mutate: requestOTP } = useMutation({
    mutationFn: () => OTPService.requestWithdrawOTP(),
    onSuccess: () => {
      setIsLoading(false);
      setPhoneNumberMissing(false);
    },
    onError: (error: any) => {
      setIsLoading(false);
      // Check if error is due to missing phone number (400 status or specific error code)
      if (
        error?.response?.data?.message &&
        error?.response?.data?.message.includes("phone number is required")
      ) {
        setPhoneNumberMissing(true);
      } else {
        toast.error(error?.response?.data?.message || t('message.otp_request_error'));
        onClose();
      }
    }
  });

  // Verify OTP mutation
  const { mutate: verifyOTP, isLoading: isVerifying } = useMutation({
    mutationFn: (code: number) => OTPService.verifyWithdrawOTP({ code }),
    onSuccess: () => {
      toast.success(t('message.otp_verified'));
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('message.otp_verification_error'));
    }
  });

  // Handle resend OTP
  const handleResendOTP = () => {
    if (canResend) {
      setCanResend(false);
      setTimeRemaining(60);
      requestOTP();
    }
  };
  */

  // If TOTP is not set up, show message with link to profile
  if (totpNotSetup) {
    return (
      <>
        <div className="general-modal__content">
          <p>{t('message.totp_not_setup')}</p>
          <p>
            {t('message.setup_totp_prompt')}{' '}
            <Link to="/user-preferences?open=totp" onClick={onClose}>
              {t('common.profile_settings')}
            </Link>
          </p>
        </div>
        <div className="general-modal__footer">
          <button
            className="cta-button cta-medium cta-blue"
            onClick={onClose}
          >
            <span>{t('common.close')}</span>
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="general-modal__content">
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <p>{t('message.enter_totp_code')}</p>
            <form className="dv-form">
              <Input
                name="code"
                label={t('common.verification_code')}
                placeholder={t('common.enter_code')}
                register={register}
                errors={errors}
                type="number"
                rules={{
                  required: t('form.required.code'),
                  validate: (value) => {
                    if (!/^\d+$/.test(value)) {
                      return t('form.invalid.code_format');
                    }
                    return true;
                  }
                }}
              />
            </form>
          </>
        )}
      </div>
      <div className="general-modal__footer">
        <button
          className="cta-button cta-medium cta-outline"
          style={{ color: 'black' }}
          onClick={onClose}
        >
          <span>{t('common.cancel')}</span>
        </button>
        <button
          disabled={!isValid || isVerifying || isLoading}
          className="cta-button cta-medium cta-blue"
          onClick={handleSubmit(onSubmit)}
        >
          <span>{t(isVerifying ? 'common.verifying...' : 'common.verify')}</span>
        </button>
      </div>
    </>
  );
};

export default WithdrawOTPModal;
